import { IStockTakeItemDetail, StockTakeItemStatus } from '../models';

/**
 * Inputs for stock take quantity and value variance — shared by the live grid and finalised figures.
 */
export interface IStockTakeVarianceInputs {
  delColMovement: number;
  delColQty: number;
  movement: number;
  onhand: number;
  qtyCounted: number;
  /** When false, Del-Col qty and movement are ignored (treated as 0). Defaults to true. */
  includeDelCol?: boolean;
}

/**
 * Whether a stock take includes Del-Col in variance and UI.
 * Legacy documents without the field are treated as enabled.
 */
export function isStockTakeIncludeDelCol(includeDelCol: boolean | null | undefined): boolean {
  return includeDelCol !== false;
}

const COUNTED_STOCK_TAKE_STATUSES: ReadonlySet<StockTakeItemStatus> = new Set([
  StockTakeItemStatus.counted,
  StockTakeItemStatus.unique,
]);

/** Minimum difference (Rand) before totals are treated as changed. */
export const STOCK_TAKE_RAND_VARIANCE_TOTAL_EPSILON = 0.005;

/**
 * Quantity variance: counted qty minus system on-hand, del-col qty, del-col movement, plus stock movement.
 * When includeDelCol is false, del-col qty and del-col movement are omitted from the formula.
 */
export function calculateStockTakeItemVariance(inputs: IStockTakeVarianceInputs): number {
  const includeDelCol = isStockTakeIncludeDelCol(inputs.includeDelCol);
  const delColQty = includeDelCol ? inputs.delColQty : 0;
  const delColMovement = includeDelCol ? inputs.delColMovement : 0;
  return (
    inputs.qtyCounted
    - inputs.onhand
    - delColQty
    - delColMovement
    + inputs.movement
  );
}

/**
 * Value variance (rand) — average cost only; stored as `randVariance` for backend legacy compatibility.
 */
export function calculateStockTakeRandVariance(variance: number, avCost: number): number {
  return variance * avCost;
}

/**
 * Total qty variance for counted and unique items — matches the detail page sub-header.
 */
export function calculateStockTakeTotalQtyVariance(items: IStockTakeItemDetail[]): number {
  return items
    .filter((item: IStockTakeItemDetail) => COUNTED_STOCK_TAKE_STATUSES.has(item.status))
    .reduce((sum: number, item: IStockTakeItemDetail) => sum + (item.variance ?? 0), 0);
}

/**
 * Total rand variance for counted and unique items — matches the detail page sub-header and confirm modal.
 */
export function calculateStockTakeTotalRandVariance(items: IStockTakeItemDetail[]): number {
  return items
    .filter((item: IStockTakeItemDetail) => COUNTED_STOCK_TAKE_STATUSES.has(item.status))
    .reduce((sum: number, item: IStockTakeItemDetail) => sum + (item.randVariance ?? 0), 0);
}

/**
 * Whether two rand variance totals differ beyond rounding tolerance.
 */
export function stockTakeRandVarianceTotalsDiffer(before: number, after: number): boolean {
  return Math.abs(after - before) >= STOCK_TAKE_RAND_VARIANCE_TOTAL_EPSILON;
}
